import React, {useCallback, useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import styled from "styled-components";
import ko from "date-fns/locale/ko";
import {
  CalendarBox,
  CalendarIcon,
  CustomDatePicker,
  DateContainer
} from "../../assets/GlobalStyles";
import {
  cancelAllowCampaignWeightSchedule,
  createAllowCampaignWeightSchedule,
  selectAllowCampaignWeightSchedules
} from "../../services/reports/mediaAxios";

const statusLabel = {
  WAITING: "예약 대기",
  PROCESSING: "적용 중",
  APPLIED: "적용 완료",
  CANCELLED: "예약 취소",
  FAILED: "적용 실패"
};
const pad = value => String(value).padStart(2, "0");
const dateText = date => `${date.getFullYear()}-${pad(
    date.getMonth() + 1)}-${pad(date.getDate())}`;

export default function HoverCampaigns({
  ids,
  onSave,
  inventoryId,
  inventoryName
}) {
  const list = useMemo(() => Array.isArray(ids) ? ids : [], [ids]);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("NOW");
  const [scheduledDate, setScheduledDate] = useState("");
  const [draft, setDraft] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const minDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  useEffect(() => {
    setDraft([...list].map(item => ({...item})).sort(
        (a, b) => (a.campaignName || "").localeCompare(b.campaignName || "",
            "ko")));
  }, [list]);

  const loadSchedules = useCallback(async () => {
    if (!inventoryId) {
      return;
    }
    setScheduleLoading(true);
    const response = await selectAllowCampaignWeightSchedules(inventoryId);
    const rows = Array.isArray(response) ? response : [];
    setSchedules(rows);
    setSelectedScheduleId(
        current => rows.some(item => item.id === current) ? current : null);
    setScheduleLoading(false);
  }, [inventoryId]);

  useEffect(() => {
    if (modalOpen) {
      loadSchedules();
    }
  }, [loadSchedules, modalOpen]);
  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }
    const close = event => event.key === "Escape" && setModalOpen(false);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [modalOpen]);

  const totalWeight = draft.reduce(
      (sum, item) => sum + (Number(item.weight) || 0), 0);
  const selectedSchedule = schedules.find(
      item => item.id === selectedScheduleId) || null;
  const selectedWeightByCampaignId = useMemo(() => new Map(
      (selectedSchedule?.items || []).map(
          item => [item.campaignId, item.weight])), [selectedSchedule]);
  const invalid = draft.some(
      item => item.weight === "" || !Number.isInteger(Number(item.weight))
          || Number(item.weight) < 0);
  const changeWeight = (key, value) => {
    setDraft(current => current.map(item => item.id === key ? {
      ...item,
      weight: value === "" ? "" : Number(value)
    } : item));
    setMessage(mode === "NOW" ? "가중치가 변경되었습니다. 즉시 적용 버튼을 눌러야 송출에 반영됩니다."
        : "가중치가 변경되었습니다. 예약 날짜를 확인한 후 예약 등록 버튼을 눌러 주세요.");
  };
  const payloadItems = draft.map(item => ({
    id: item.id,
    campaignId: item.campaignId,
    weight: Number(item.weight)
  }));

  const save = async () => {
    setMessage("");
    if (invalid) {
      setMessage("가중치는 0 이상의 정수로 입력해 주세요.");
      return;
    }
    if (mode === "SCHEDULE" && !scheduledDate) {
      setMessage("예약 적용 날짜를 선택해 주세요.");
      return;
    }
    setSaving(true);
    if (mode === "NOW") {
      const result = typeof onSave === "function" ? await onSave(draft) : false;
      if (result !== false) {
        window.alert("캠페인 가중치를 즉시 적용했습니다.");
        setModalOpen(false);
      } else {
        setMessage("가중치 즉시 적용에 실패했습니다.");
      }
    } else {
      const result = await createAllowCampaignWeightSchedule({
        inventoryId, scheduledDate, inventoryAllowCampaignInfoList: payloadItems
      });
      if (result) {
        window.alert(`${scheduledDate} 가중치 예약을 등록했습니다.`);
        setMessage("");
        setScheduledDate("");
        await loadSchedules();
      } else {
        setMessage("예약 등록에 실패했습니다. API 연결 상태를 확인해 주세요.");
      }
    }
    setSaving(false);
  };

  const cancelSchedule = async id => {
    if (!window.confirm("선택한 가중치 예약을 취소하시겠습니까?")) {
      return;
    }
    const result = await cancelAllowCampaignWeightSchedule(id);
    if (result) {
      await loadSchedules();
    } else {
      setMessage("예약 취소에 실패했습니다.");
    }
  };

  if (!list.length) {
    return <span>-</span>;
  }
  return <>
    <CountButton type="button"
                 onClick={event => {
                   event.preventDefault();
                   event.stopPropagation();
                   setModalOpen(true);
                 }}>{list.length}개</CountButton>
    {modalOpen && createPortal(<Overlay onMouseDown={() => setModalOpen(false)}>
      <Dialog onMouseDown={event => event.stopPropagation()}>
        <Header>
          <div><Accent/><strong>캠페인 가중치 설정</strong><span>{inventoryName
              || "선택 지면"}의 연결 캠페인 송출 비중을 관리합니다.</span></div>
          <Close type="button"
                 onClick={() => setModalOpen(false)}>×</Close></Header>
        <Body>
          <ModeTabs role="tablist"><ModeTab type="button" role="tab"
                                            aria-selected={mode === "NOW"}
                                            $active={mode === "NOW"}
                                            onClick={() => {
                                              setMode("NOW");
                                              setMessage("");
                                            }}><b>즉시 적용</b><span>저장 즉시 송출 가중치를 변경합니다.</span></ModeTab>
            <ModeTab type="button" role="tab"
                     aria-selected={mode === "SCHEDULE"}
                     $active={mode === "SCHEDULE"} onClick={() => {
              setMode("SCHEDULE");
              setMessage("");
            }}><TabTitle><b>예약 적용</b><TabCount $active={mode === "SCHEDULE"}>{schedules.length}건</TabCount></TabTitle><span>선택한 날짜에 전체 가중치를 교체합니다.</span></ModeTab></ModeTabs>
          {mode === "SCHEDULE" && <ScheduleBar>
            <div><label htmlFor="weight-scheduled-date">예약 적용 날짜</label><span>오늘 이후 날짜를 선택해 주세요.</span></div>
            <ScheduleDateContainer><CalendarBox><CalendarIcon/></CalendarBox><CustomDatePicker
                id="weight-scheduled-date" showIcon={false}
                selected={scheduledDate ? new Date(`${scheduledDate}T00:00:00`) : null}
                minDate={minDate}
                onChange={date => setScheduledDate(date ? dateText(date) : "")}
                locale={ko} dateFormat="yyyy-MM-dd" placeholderText="예약 날짜 선택"
                isClearable={false} popperPlacement="bottom-end"
                calendarContainer={({className, children}) =>
                    <SingleCalendarPanel className={className}>{children}</SingleCalendarPanel>}/>
            </ScheduleDateContainer>
          </ScheduleBar>}
          <WeightWorkspace $schedule={mode === "SCHEDULE"}>
            <WeightPanel><SectionHead>
              <div><strong>캠페인별 가중치</strong><span>예약 적용 시 아래 목록 전체가 하나의 스냅샷으로 저장됩니다.</span>
              </div>
            </SectionHead>
              <CampaignTable><TableHeader $comparison={mode
                  === "SCHEDULE"}><span>캠페인</span><WeightColumnHeader><span>설정 가중치</span><b>{totalWeight.toLocaleString()}</b></WeightColumnHeader>{mode
                  === "SCHEDULE" &&
                  <ReservedHeader><span><i/>예약 가중치</span><b>{selectedSchedule
                      ? Number(
                          selectedSchedule.totalWeight ?? 0).toLocaleString()
                      : "-"}</b></ReservedHeader>}</TableHeader><TableBody>
                {draft.map((item, index) => <CampaignRow
                    $comparison={mode === "SCHEDULE"}
                    key={item.id || item.campaignId
                        || index}><CampaignInfo><i>{index + 1}</i>
                  <div><strong>{item.campaignName
                      || "-"}</strong><span>{item.campaignId
                      || "캠페인 ID 없음"}</span></div>
                </CampaignInfo><WeightInput type="number" min="0" step="1"
                                            value={item.weight ?? ""}
                                            onChange={event => changeWeight(
                                                item.id,
                                                event.target.value)}/>{mode
                    === "SCHEDULE" && <ReservedWeight
                        $active={selectedWeightByCampaignId.has(
                            item.campaignId)}>{selectedWeightByCampaignId.has(
                        item.campaignId) ? Number(
                        selectedWeightByCampaignId.get(item.campaignId)
                        || 0).toLocaleString() : "-"}</ReservedWeight>}
                </CampaignRow>)}
              </TableBody></CampaignTable>
            </WeightPanel>
            {mode === "SCHEDULE" && <ReservationSection><SectionHead>
              <div><strong>예약 가중치 목록</strong><span>예약을 선택하면 저장된 캠페인별 가중치를 확인할 수 있습니다.</span>
              </div>
              <ScheduleCount>{schedules.length}건</ScheduleCount></SectionHead>
              <ScheduleList>{scheduleLoading ? <Empty>예약 정보를 불러오고 있습니다.</Empty>
                  : schedules.length ? schedules.map(
                      schedule => <ScheduleRow key={schedule.id}
                                               $selected={selectedScheduleId
                                                   === schedule.id}
                                               role="button" tabIndex="0"
                                               aria-pressed={selectedScheduleId
                                                   === schedule.id}
                                               onClick={() => setSelectedScheduleId(
                                                   current => current
                                                   === schedule.id ? null
                                                       : schedule.id)}
                                               onKeyDown={event => {
                                                 if (event.key === "Enter"
                                                     || event.key === " ") {
                                                   event.preventDefault();
                                                   setSelectedScheduleId(
                                                       current => current
                                                       === schedule.id ? null
                                                           : schedule.id);
                                                 }
                                               }}>
                        <ScheduleMain><ScheduleArrow $open={selectedScheduleId
                            === schedule.id}>›</ScheduleArrow>
                          <div>
                            <strong>{schedule.scheduledDate}</strong><span>{schedule.itemCount
                              ?? schedule.items?.length ?? 0}개 캠페인 · 합계 {Number(
                              schedule.totalWeight
                              ?? 0).toLocaleString()}</span></div>
                        </ScheduleMain><Status
                          $status={schedule.status}>{statusLabel[schedule.status]
                          || schedule.status}</Status>{schedule.status
                          === "WAITING" && <CancelSchedule type="button"
                                                           onClick={event => {
                                                             event.stopPropagation();
                                                             cancelSchedule(
                                                                 schedule.id);
                                                           }}>예약
                            취소</CancelSchedule>}</ScheduleRow>) : <Empty>등록된 가중치
                    예약이 없습니다.</Empty>}
              </ScheduleList>
              {selectedSchedule ?
                  <SelectedScheduleGuide><span><b>{selectedSchedule.scheduledDate}</b> 예약 가중치를 좌측 테이블에 표시하고 있습니다.</span><strong>합계 {Number(
                      selectedSchedule.totalWeight
                      ?? 0).toLocaleString()}</strong></SelectedScheduleGuide> :
                  <ReservationEmpty>예약을 선택하면 좌측 테이블의 예약 가중치 컬럼에
                    표시됩니다.</ReservationEmpty>}
            </ReservationSection>}
          </WeightWorkspace>
          {message && !message.includes("가중치가 변경되었습니다") && <Message
              $error={message.includes("실패") || message.includes("입력")
                  || message.includes("선택")}
              $notice={message.includes("변경되었습니다")}><MessageIcon
              $error={message.includes("실패") || message.includes("입력")
                  || message.includes("선택")}
              $notice={message.includes("변경되었습니다")}>{message.includes("변경되었습니다")
              ? "!" : message.includes("실패") || message.includes("입력")
              || message.includes("선택") ? "!"
                  : "✓"}</MessageIcon><span>{message}</span></Message>}
        </Body>
        <Footer><FooterGuide><strong>{mode === "NOW" ? "즉시 가중치 적용"
            : "가중치 예약 등록"}</strong><span>{mode === "NOW"
            ? "변경 사항이 즉시 송출에 반영됩니다."
            : "예약 날짜에 전체 캠페인 가중치가 교체됩니다."}</span></FooterGuide>
          <div>{message.includes("가중치가 변경되었습니다") && <FooterChangeNotice><i>!</i><span>{message}</span></FooterChangeNotice>}<Secondary type="button" onClick={() => setModalOpen(
              false)}>취소</Secondary><Primary type="button" disabled={saving}
                                             onClick={save}>{saving ? "처리 중"
              : mode === "NOW" ? "즉시 적용" : "예약 등록"}</Primary></div>
        </Footer>
      </Dialog>
    </Overlay>, document.body)}
  </>;
}

const CountButton = styled.button`
  display: inline-flex;
  height: 28px;
  min-width: 50px;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 1px solid #93bce8;
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-primary-strong);
  font-size: var(--font-size-10);
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(37, 99, 235, .08);
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease, transform .15s ease;

  &::after {
    margin-left: 5px;
    font-size: var(--font-size-11);
    content: "›";
  }

  &:hover {
    border-color: var(--color-primary-strong);
    background: #eaf3ff;
    box-shadow: 0 4px 9px rgba(37, 99, 235, .14);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-focus);
    outline-offset: 2px;
  }
`;
const Overlay = styled.div`position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, .5);
  backdrop-filter: blur(4px);`;
const Dialog = styled.div`position: relative;
  display: flex;
  width: min(1120px, 96vw);
  max-height: calc(100vh - 36px);
  overflow: hidden;
  flex-direction: column;
  border: 1px solid var(--color-border-strong);
  border-radius: 13px;
  background: var(--color-surface);
  box-shadow: 0 24px 65px rgba(15, 23, 42, .28);`;
const Header = styled.header`display: flex;
  min-height: 72px;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid #e7ebef;
  background: linear-gradient(135deg, var(--color-surface), #f5f9ff);

  > div {
    position: relative;
    padding-left: 13px;
  }

  strong, span {
    display: block;
  }

  strong {
    color: #252b33;
    font-size: var(--font-size-15);
    font-weight: 800;
  }

  span {
    margin-top: 5px;
    color: #89929d;
    font-size: var(--font-size-9);
  }`;
const Accent = styled.i`position: absolute;
  top: 1px;
  bottom: 1px;
  left: 0;
  width: 3px;
  border-radius: 3px;
  background: var(--color-primary-strong);`;
const Close = styled.button`width: 30px;
  height: 30px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #7c858f;
  font-size: var(--font-size-22);

  &:hover {
    background: #edf3fa;
    color: var(--color-primary-strong);
  }`;
const Body = styled.main`min-height: 0;
  padding: 18px 22px;
  overflow: auto;`;
const ModeTabs = styled.div`
  display: grid;
  grid-template-columns:1fr 1fr;
  margin: -18px -22px 18px;
  border-bottom: 1px solid var(--color-border-strong);
  background: #f8fafc;
`;
const ModeTab = styled.button`
  position: relative;
  min-height: 64px;
  padding: 11px 20px;
  border: 0;
  border-right: 1px solid #e3e8ed;
  background: ${({$active}) => $active ? "var(--color-surface)" : "transparent"};
  text-align: center;

  &:last-child {
    border-right: 0;
  }

  &::after {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 3px;
    background: ${({$active}) => $active ? "var(--color-primary-strong)" : "transparent"};
    content: "";
  }

  b, span {
    display: block;
  }

  b {
    color: ${({$active}) => $active ? "var(--color-primary-strong)" : "#6e7883"};
    font-size: var(--font-size-11);
    font-weight: ${({$active}) => $active ? 800 : 650};
  }

  span {
    margin-top: 5px;
    color: ${({$active}) => $active ? "#687787" : "#9aa2aa"};
    font-size: var(--font-size-8);
  }

  &:hover {
    background: ${({$active}) => $active ? "var(--color-surface)" : "#f2f6fb"};
  }

  &:focus-visible {
    z-index: 1;
    outline: 2px solid var(--color-primary-focus);
    outline-offset: -2px;
  }
`;
const TabTitle = styled.div`display:flex;align-items:center;justify-content:center;gap:7px;`;
const TabCount = styled.span`display:inline-flex!important;min-width:25px;height:19px;align-items:center;justify-content:center;margin:0!important;padding:0 6px;border:1px solid ${({$active})=>$active?"#93bce8":"#d7dee6"};border-radius:999px;background:${({$active})=>$active?"#eaf3ff":"#f1f3f5"};color:${({$active})=>$active?"var(--color-primary-strong)":"#7f8994"}!important;font-size: var(--font-size-8)!important;font-weight:800;`;
const ScheduleBar = styled.div`display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #dbe6f3;
  border-radius: 9px;
  background: var(--color-surface-muted);

  label, span {
    display: block;
  }

  label {
    color: #34404d;
    font-size: var(--font-size-10);
    font-weight: 750;
  }

  span {
    margin-top: 4px;
    color: #8c96a0;
    font-size: var(--font-size-8);
  }

  @media (max-width: 500px) {
    align-items: stretch;
    flex-direction: column;
  }`;
const ScheduleDateContainer = styled(DateContainer)`position: relative;
  width: 210px;
  height: 42px;
  flex: 0 0 210px;
  overflow: visible;
  border-color: var(--color-border-strong);
  border-radius: 8px;
  background: var(--color-surface);
  transition: border-color .15s ease, box-shadow .15s ease;

  &:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1);
  }

  ${CalendarBox} {
    display: inline-flex;
    width: 40px;
    flex: 0 0 40px;
    border-right: 1px solid var(--color-divider);
    border-radius: 7px 0 0 7px;
    background: #f8fafc;
    color: #64748b;
  }

  ${CalendarIcon} {
    width: 15px;
    height: 16px;
    opacity: .8;
  }

  ${CustomDatePicker} {
    height: 40px;
    padding: 0 12px;
    border: 0 !important;
    border-radius: 0 7px 7px 0;
    background: transparent;
    color: var(--color-text);
    font-size: var(--font-size-10);
    outline: none;
  }

  .react-datepicker-wrapper, .react-datepicker__input-container {
    display: block;
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 30;
  }

  @media (max-width: 500px) {
    width: 100%;
    flex-basis: auto;
  }`;
const SingleCalendarPanel = styled.div`overflow: hidden;
  border: 1px solid #dfe3e7 !important;
  border-radius: 10px !important;
  background: var(--color-surface);
  box-shadow: 0 12px 30px rgba(31, 41, 55, .16);
  font-family: inherit;

  .react-datepicker__header {
    padding-top: 12px;
    border-bottom-color: var(--color-divider);
    background: var(--color-surface);
  }

  .react-datepicker__current-month {
    color: #303842;
    font-size: var(--font-size-11);
    font-weight: 800;
  }

  .react-datepicker__day-name, .react-datepicker__day {
    font-size: var(--font-size-9);
  }

  .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
    border-radius: 6px;
    background: var(--color-primary);
    color: var(--color-surface);
  }

  .react-datepicker__day:hover {
    border-radius: 6px;
    background: var(--color-primary-muted);
    color: var(--color-main);
  }

  .react-datepicker__navigation {
    top: 8px;
    border-radius: 6px;
  }

  .react-datepicker__navigation:hover {
    background: var(--color-primary-soft);
  }`;
const SectionHead = styled.div`display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 3px 0 10px;

  strong, span {
    display: block;
  }

  strong {
    color: #303842;
    font-size: var(--font-size-11);
    font-weight: 750;
  }

  span {
    margin-top: 4px;
    color: #8b949f;
    font-size: var(--font-size-8);
  }`;
const WeightWorkspace = styled.div`display: grid;
  grid-template-columns:${({$schedule}) => $schedule
          ? "minmax(0,1fr) minmax(0,1fr)" : "minmax(0,1fr)"};
  gap: 18px;
  align-items: start;
  @media (max-width: 840px) {
    grid-template-columns:minmax(0, 1fr);
  }`;
const WeightPanel = styled.section`min-width: 0;`;
const CampaignTable = styled.div`overflow: hidden;
  border: 1px solid #dfe5ea;
  border-radius: 9px;`;
const TableHeader = styled.div`display: grid;
  grid-template-columns:${({$comparison}) => $comparison
          ? "minmax(0,1fr) 120px 120px" : "minmax(0,1fr) 150px"};
  height: 46px;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid #cddff5;
  background: #f2f7fd;
  color: #52606f;
  font-size: var(--font-size-9);
  font-weight: 750;

  > span:first-child {
    text-align: left;
  }

  @media (max-width: 520px) {
    grid-template-columns:${({$comparison}) => $comparison
            ? "minmax(0,1fr) 82px 82px" : "minmax(0,1fr) 90px"};
    padding: 0 10px;
  }`;
const WeightColumnHeader = styled.div`display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #52606f;

  span {
    font-size: var(--font-size-9);
  }

  b {
    display: inline-flex;
    min-width: 28px;
    height: 20px;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--color-surface);
    color: var(--color-primary-strong);
    font-size: var(--font-size-9);
    box-shadow: 0 1px 3px rgba(37, 99, 235, .1);
  }`;
const ReservedHeader = styled.div`display: flex;
  height: 34px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-left: 7px;
  border: 1px solid #93bce8;
  border-radius: 6px;
  background: #e3effc;
  color: #1d5fae;
  font-weight: 800;

  span {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: var(--font-size-9);
  }

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-primary-strong);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
  }

  b {
    display: inline-flex;
    min-width: 25px;
    height: 20px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--color-primary-strong);
    color: var(--color-surface);
    font-size: var(--font-size-9);
  }`;
const TableBody = styled.div`height: 280px;
  overflow: auto;`;
const FooterChangeNotice = styled.div`display: flex;
  min-height: 34px;
  max-width: 330px;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 1px solid var(--color-primary-border);
  border-radius: 7px;
  background: var(--color-primary-soft);
  color: #245f9f;
  font-size: var(--font-size-9);
  font-weight: 650;

  i {
    display: flex;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--color-primary-strong);
    color: var(--color-surface);
    font-size: var(--font-size-9);
    font-style: normal;
    font-weight: 850;
  }

  span { line-height: 1.35; }
`;
const CampaignRow = styled.div`display: grid;
  grid-template-columns:${({$comparison}) => $comparison
          ? "minmax(0,1fr) 120px 120px" : "minmax(0,1fr) 150px"};
  min-height: 56px;
  align-items: center;
  gap: ${({$comparison}) => $comparison ? "8px" : "0"};
  padding: 8px 16px;
  border-bottom: 1px solid var(--color-divider);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 520px) {
    grid-template-columns:${({$comparison}) => $comparison
            ? "minmax(0,1fr) 82px 82px" : "minmax(0,1fr) 90px"};
    gap: 6px;
    padding: 8px 10px;
  }`;
const CampaignInfo = styled.div`display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;

  i {
    display: flex;
    width: 25px;
    height: 25px;
    flex: 0 0 25px;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #edf3fa;
    color: #59738f;
    font-size: var(--font-size-8);
    font-style: normal;
    font-weight: 800;
  }

  div {
    min-width: 0;
  }

  strong, span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #343c46;
    font-size: var(--font-size-10);
  }

  span {
    margin-top: 3px;
    color: #9aa2aa;
    font-size: var(--font-size-8);
  }`;
const WeightInput = styled.input`width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d8dfe6;
  border-radius: 7px;
  color: #34404d;
  font-size: var(--font-size-10);
  text-align: right;
  outline: 0;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1);
  }`;
const ReservedWeight = styled.b`display: flex;
  height: 34px;
  align-items: center;
  justify-content: flex-end;
  padding: 0 11px;
  border: 1px solid ${({$active}) => $active ? "#72a9e2" : "#d8e4f0"};
  border-radius: 7px;
  background: ${({$active}) => $active
          ? "linear-gradient(135deg,#e6f1ff,#dcecff)" : "#f5f8fb"};
  color: ${({$active}) => $active ? "#155cad" : "#9aa8b6"};
  font-size: var(--font-size-11);
  font-weight: 850;
  box-shadow: ${({$active}) => $active
          ? "inset 3px 0 0 var(--color-primary-strong),0 2px 6px rgba(37,99,235,.1)" : "none"};`;
const ReservationSection = styled.section`min-width: 0;
  padding-top: 2px;`;
const ScheduleCount = styled.b`padding: 5px 8px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-8);`;
const ScheduleList = styled.div`max-height: 122px;
  overflow: auto;
  border: 1px solid #e1e6eb;
  border-radius: 9px;`;
const ScheduleRow = styled.div`display: grid;
  grid-template-columns:minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  min-height: 55px;
  padding: 9px 13px;
  border-bottom: 1px solid var(--color-divider);
  background: ${({$selected}) => $selected ? "#f2f7fd" : "var(--color-surface)"};
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({$selected}) => $selected ? "#edf5ff" : "#fafcff"};
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-focus);
    outline-offset: -2px;
  }

  strong, span {
    display: block;
  }

  strong {
    color: #35404c;
    font-size: var(--font-size-10);
  }

  span {
    margin-top: 4px;
    color: #9099a3;
    font-size: var(--font-size-8);
  }`;
const ScheduleMain = styled.div`display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;

  > div {
    min-width: 0;
  }`;
const ScheduleArrow = styled.i`display: flex;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  background: ${({$open}) => $open ? "var(--color-primary-muted)" : "#f0f3f6"};
  color: ${({$open}) => $open ? "var(--color-primary-strong)" : "#7e8995"};
  font-size: var(--font-size-15);
  font-style: normal;
  transform: rotate(${({$open}) => $open ? "90deg" : "0deg"});
  transition: transform .15s ease;`;
const Status = styled.b`padding: 5px 8px;
  border-radius: 999px;
  background: ${({$status}) => $status === "APPLIED" ? "#edf9f3" : $status
  === "FAILED" ? "#fff1f1" : $status === "CANCELLED" ? "#f3f4f6" : "var(--color-primary-soft)"};
  color: ${({$status}) => $status === "APPLIED" ? "#168253" : $status
  === "FAILED" ? "#d04444" : $status === "CANCELLED" ? "#7a838d" : "var(--color-primary-strong)"};
  font-size: var(--font-size-8);
  white-space: nowrap;`;
const CancelSchedule = styled.button`height: 28px;
  padding: 0 9px;
  border: 1px solid #e1c2c2;
  border-radius: 6px;
  background: var(--color-surface);
  color: #c54a4a;
  font-size: var(--font-size-8);
  font-weight: 700;`;
const Empty = styled.div`padding: 22px;
  color: #929ba5;
  font-size: var(--font-size-9);
  text-align: center;`;
const SelectedScheduleGuide = styled.div`display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #cddff5;
  border-radius: 8px;
  background: #f6faff;
  color: #697583;
  font-size: var(--font-size-8);

  b, strong {
    color: var(--color-primary-strong);
  }

  strong {
    font-size: var(--font-size-10);
    white-space: nowrap;
  }`;
const ReservationEmpty = styled.div`display: flex;
  height: 132px;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  border: 1px dashed #cfd9e4;
  border-radius: 9px;
  background: #fafcff;
  color: #8d98a4;
  font-size: var(--font-size-9);`;
const Message = styled.div`display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 11px;
  border: 1px solid ${({$error, $notice}) => $error ? "#f1c5c5" : $notice
          ? "var(--color-primary-border)" : "#bee3d1"};
  border-radius: 7px;
  background: ${({$error, $notice}) => $error ? "#fff2f2" : $notice ? "var(--color-primary-soft)"
          : "#edf9f3"};
  color: ${({$error, $notice}) => $error ? "#c94545" : $notice ? "#245f9f"
          : "#168253"};
  font-size: var(--font-size-9);
  font-weight: 650;`;
const MessageIcon = styled.i`display: flex;
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({$error, $notice}) => $error ? "#d95555" : $notice ? "var(--color-primary-strong)"
          : "#27a56c"};
  color: var(--color-surface);
  font-size: var(--font-size-10);
  font-style: normal;
  font-weight: 850;`;
const Footer = styled.footer`display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 22px;
  border-top: 1px solid #e7ebef;
  background: #f8fafc;

  > div:last-child {
    display: flex;
    gap: 8px;
  }

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }`;
const FooterGuide = styled.div`strong, span {
  display: block;
}

  strong {
    color: #35404c;
    font-size: var(--font-size-10);
  }

  span {
    margin-top: 4px;
    color: #8d96a0;
    font-size: var(--font-size-8);
  }`;
const Secondary = styled.button`min-width: 96px;
  height: 38px;
  border: 1px solid #cfd6dd;
  border-radius: 7px;
  background: var(--color-surface);
  color: #626c77;
  font-size: var(--font-size-10);
  font-weight: 700;`;
const Primary = styled.button`min-width: 110px;
  height: 38px;
  border: 1px solid var(--color-primary-strong);
  border-radius: 7px;
  background: var(--color-primary-strong);
  color: var(--color-surface);
  font-size: var(--font-size-10);
  font-weight: 750;

  &:disabled {
    border-color: #a9bdd8;
    background: #a9bdd8;
    cursor: wait;
  }`;
